import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./iCourierReport.css";
import authStore from "../store/AuthStore";
import DateRangePicker from "../component/Picker";

import excelIcon from "../assets/icons/excel.png";
import pdfIcon from "../assets/icons/pdf.png";

function ICourierReport() {
  // =========================
  // DATE STATES
  // =========================

  const [date, setDate] = useState("");
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
      .toISOString()
      .split("T")[0],
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const baseUrl =
    authStore.authData?.url ||
    JSON.parse(sessionStorage.getItem("authData"))?.url;
  // =========================
  // FILTER STATES
  // =========================

  const [statusFilter, setStatusFilter] = useState("All Status");
  const [directionToggle, setDirectionToggle] = useState("Inward"); // Default "Inward"
  const [searchTerm, setSearchTerm] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const handleDateChange = (start, end) => {
    setStartDate(start || "");
    setEndDate(end || "");
  };
  const [dashboardData, setDashboardData] = useState({
    total_entries: 0,
    intra_company: 0,
    external_inbound: 0,
    external_outbound: 0,
    pending: 0,
  });
  const statusOptions = [
    "All Status",
    "Scheduled",
    "AtGate",
    "Dispatched",
    "Delivered",
  ];

  useEffect(() => {
    const now = new Date();
    const formattedDate = now.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    setDate(formattedDate);
  }, []);

  const fetchCourierLogs = useCallback(async () => {
    try {
      setLoading(true);

      const formData = new URLSearchParams();

      formData.append("from_date", startDate || "");
      formData.append("to_date", endDate || "");

      if (statusFilter !== "All Status") {
        formData.append("status", statusFilter);
      }

      formData.append("direction", directionToggle === "Inward" ? 1 : 2);

      let headers = {
        "Content-Type": "application/x-www-form-urlencoded",
        ...(await authStore.getAuthHeader()),
      };

      let response;

      try {
        response = await axios.post(
          `${baseUrl}/api/icourier_log_report`,
          formData,
          { headers },
        );
      } catch (error) {
        const message = (error.response?.data?.message || "").toLowerCase();

        if (message.includes("invalid token")) {
          headers = {
            "Content-Type": "application/x-www-form-urlencoded",
            ...(await authStore.refreshToken()),
          };

          response = await axios.post(
            `${baseUrl}/api/icourier_log_report`,
            formData,
            { headers },
          );
        } else {
          throw error;
        }
      }

      if (response?.data?.data && Array.isArray(response.data.data)) {
        const formattedData = response.data.data.map((item, index) => ({
          id: index + 1,

          srNo: item.sr_no || "-",

          trackingNo: item.tracking_no || "-",

          type: item.type_of_courier || "-",

          courierCompany: item.courier_company || "-",

          sender: item.sender || "-",

          senderDept: item.sender_dept || "-",

          receiver: item.receiver || "-",

          receiverDept: item.receiver_dept || "-",

          weight: item.weight_kg || "-",

          charges: item.charges || "-",

          status: item.status || "-",

          receivedBy: item.received_by || "-",

          direction: item.direction || "-",

          remarks: item.remarks || "-",

          content: item.content || "-",

          createdAt: item.created_at || "-",

          dispatchDate: item.date || "-",

          dispatchTime: item.time || "-",

          deliveredDate: item.delivered_date || "-",

          deliveredTime: item.delivered_date
            ? item.delivered_date.split(" ")[1]
            : "",
        }));

        setRecords(formattedData);
      } else {
        setRecords([]);
      }
    } catch (err) {
      console.error("API Error:", err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, statusFilter, directionToggle, baseUrl]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const formData = new URLSearchParams();

      formData.append("from_date", startDate || "");
      formData.append("to_date", endDate || "");

      if (statusFilter !== "All Status") {
        formData.append("status", statusFilter);
      }

      formData.append("direction", directionToggle === "Inward" ? 1 : 2);
      let headers = {
        "Content-Type": "application/x-www-form-urlencoded",
        ...(await authStore.getAuthHeader()),
      };

      let response;

      try {
        response = await axios.post(
          `${baseUrl}/api/icourier_dashboard`,
          formData,
          { headers },
        );
      } catch (error) {
        const message = (error.response?.data?.message || "").toLowerCase();

        if (message.includes("invalid token")) {
          headers = {
            "Content-Type": "application/x-www-form-urlencoded",
            ...(await authStore.refreshToken()),
          };

          response = await axios.post(
            `${baseUrl}/api/icourier_dashboard`,
            formData,
            { headers },
          );
        } else {
          throw error;
        }
      }

      if (response?.data?.data) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.log("DASHBOARD API ERROR :", error);
    }
  }, [startDate, endDate, statusFilter, directionToggle, baseUrl]);
  useEffect(() => {
    fetchCourierLogs();
    fetchDashboardData();
  }, [fetchCourierLogs, fetchDashboardData]);
  const fetchExportData = async () => {
    try {
      const formData = new URLSearchParams();

      formData.append("from_date", startDate || "");
      formData.append("to_date", endDate || "");

      if (statusFilter !== "All Status") {
        formData.append("status", statusFilter);
      }

      formData.append("direction", directionToggle === "Inward" ? 1 : 2);
      let headers = {
        "Content-Type": "application/x-www-form-urlencoded",
        ...(await authStore.getAuthHeader()),
      };

      let response;

      try {
        response = await axios.post(
          `${baseUrl}/api/icourier_log_report`,
          formData,
          { headers },
        );
      } catch (error) {
        const message = (error.response?.data?.message || "").toLowerCase();

        if (message.includes("invalid token")) {
          headers = {
            "Content-Type": "application/x-www-form-urlencoded",
            ...(await authStore.refreshToken()),
          };

          response = await axios.post(
            `${baseUrl}/api/icourier_log_report`,
            formData,
            { headers },
          );
        } else {
          throw error;
        }
      }

      return response?.data?.data || [];
    } catch (error) {
      console.error("EXPORT ERROR :", error);
      return [];
    }
  };

  const exportExcel = async () => {
    const data = await fetchExportData();
    if (!data.length) return;

    const formattedData = data.map((item, index) => ({
      "#": index + 1,
      DATE: item.date || "-",
      TIME: item.time || "-",
      TYPE: item.type_of_courier || "-",
      COMPANY: item.courier_company || "-",
      SENDER: item.sender || "-",
      RECEIVER: item.receiver || "-",
      STATUS: item.status || "-",
      DIRECTION: item.direction || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Courier Report");
    XLSX.writeFile(workbook, `icourier_report_${Date.now()}.xlsx`);
  };

  const exportPDF = async () => {
    const data = await fetchExportData();
    if (!data.length) return;

    const doc = new jsPDF("landscape");
    const tableColumn = [
      "#",
      "DATE",
      "TIME",
      "TYPE",
      "COMPANY",
      "SENDER",
      "RECEIVER",
      "STATUS",
      "DIRECTION",
    ];

    const tableRows = data.map((item, index) => [
      index + 1,
      item.date || "-",
      item.time || "-",
      item.type || "-",
      item.courier_company || "-",
      item.sender || "-",
      item.receiver || "-",
      item.status || "-",
      item.direction || "-",
    ]);

    doc.text("ICourier Report", 14, 15);
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });

    window.open(doc.output("bloburl"), "_blank");
  };
  const formatDate = (dateString) => {
    if (!dateString || dateString === "-") return "-";

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);

    return `${day}/${month}/${year}`;
  };
  // Filter records based on direction toggle and search term
  const filteredRecords = records.filter((record) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      record.type?.toLowerCase().includes(term) ||
      record.sender?.toLowerCase().includes(term) ||
      record.receiver?.toLowerCase().includes(term) ||
      record.courierCompany?.toLowerCase().includes(term) ||
      record.status?.toLowerCase().includes(term) ||
      record.direction?.toLowerCase().includes(term) ||
      record.content?.toLowerCase().includes(term) ||
      record.trackingNo?.toLowerCase().includes(term)
    );
  });

  return (
    <div className="courier-container">
      {/* HEADER */}
      <div className="courier-dashboard-header">
        <div>
          <h1 className="courier-dashboard-title">Courier Activity</h1>
          <p className="courier-dashboard-subtitle">
            Courier movement for:
            {startDate && endDate ? `${startDate} To ${endDate}` : date}
          </p>
        </div>

        <div className="courier-dashboard-picker">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
          />
        </div>
      </div>

      {/* STATS */}
      <div className="courier-stats-grid">
        <div className="courier-card courier-blue">
          <div className="courier-card-top">
            <h4>TOTAL</h4>
            <h2>{dashboardData.total_entries}</h2>
          </div>
          <p>All courier logs</p>
        </div>

        <div className="courier-card courier-green">
          <div className="courier-card-top">
            <h4>INTRA COMPANY</h4>
            <h2>{dashboardData.intra_company}</h2>
          </div>
          <p>Internal courier</p>
        </div>

        <div className="courier-card courier-cyan">
          <div className="courier-card-top">
            <h4>EXTERNAL INBOUND</h4>
            <h2>{dashboardData.external_inbound}</h2>
          </div>
          <p>Incoming courier</p>
        </div>

        <div className="courier-card courier-red">
          <div className="courier-card-top">
            <h4>EXTERNAL OUTBOUND</h4>
            <h2>{dashboardData.external_outbound}</h2>
          </div>
          <p>Outgoing courier</p>
        </div>

        <div className="courier-card courier-yellow">
          <div className="courier-card-top">
            <h4>PENDING</h4>
            <h2>{dashboardData.pending}</h2>
          </div>
          <p>Pending courier</p>
        </div>
      </div>

      {/* TABLE */}
      <div className="courier-table-card">
        <div className="courier-table-header">
          <div className="courier-table-title-row">
            <input
              type="text"
              className="courier-table-search"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="courier-table-actions">
            <div className="courier-table-left-actions">
              <button
                className="courier-action-btn courier-export-btn"
                onClick={exportExcel}
                title="Export to Excel"
              >
                <img
                  src={excelIcon}
                  alt="Export to Excel"
                  style={{ width: "14px", height: "14px" }}
                />
              </button>

              <button
                className="courier-action-btn courier-print-btn"
                onClick={exportPDF}
                title="Export to PDF"
              >
                <img
                  src={pdfIcon}
                  alt="Export to PDF"
                  style={{ width: "14px", height: "14px" }}
                />
              </button>
            </div>

            <div className="courier-table-right-actions">
              <select
                className="courier-table-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map((status, i) => (
                  <option key={i} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              {/* Direction Toggle Switch */}
              <div className="direction-toggle-switch">
                <span
                  className={directionToggle === "Inward" ? "active-label" : ""}
                >
                  Inward
                </span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={directionToggle === "Outward"}
                    onChange={() =>
                      setDirectionToggle(
                        directionToggle === "Inward" ? "Outward" : "Inward",
                      )
                    }
                  />
                  <span className="slider"></span>
                </label>
                <span
                  className={
                    directionToggle === "Outward" ? "active-label" : ""
                  }
                >
                  Outward
                </span>
              </div>

              {/* Status Filter Dropdown */}
            </div>
          </div>
        </div>

        <div className="courier-table-wrapper">
          <table className="courier-table">
            <thead>
              <tr>
                <th>#</th>
                <th>DIRECTION</th>
                <th>ROUTE</th>
                <th>TYPE</th>
                <th>TRACKING NO</th>
                <th>WEIGHT(kg)</th>
                <th>SENDER</th>
                <th>SENDER LOCATION</th>
                <th>RECIPIENT</th>
                <th>RECIPIENT LOCATION</th>
                <th>DISPATCH DATE</th>
                <th>DISPATCH TIME</th>
                <th>DELIVERED DATE</th>
                <th>DELIVERED TIME</th>
                <th>STATUS</th>
                <th>REMARKS</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={16} className="courier-no-data">
                    Loading...
                  </td>
                </tr>
              ) : filteredRecords.length > 0 ? (
                filteredRecords.map((record, index) => (
                  <tr key={record.id}>
                    <td>{index + 1}</td>
                    <td>
                      <span
                        className={`courier-badge ${
                          record.direction === "Inward"
                            ? "courier-employee"
                            : "courier-visitor"
                        }`}
                      >
                        {record.direction}
                      </span>
                    </td>
                    <td>{record.courierCompany}</td>
                    <td>
                      <span className="courier-badge courier-purpose-badge">
                        {record.type}
                      </span>
                    </td>
                    <td>{record.trackingNo}</td>
                    <td>{record.weight}</td>
                    <td>{record.sender}</td>
                    <td>{record.senderDept}</td>
                    <td>{record.receiver}</td>
                    <td>{record.receiverDept}</td>
                    <td>{formatDate(record.dispatchDate)}</td>
                    <td>{record.dispatchTime}</td>
                    <td>{formatDate(record.deliveredDate)}</td>
                    <td>{record.deliveredTime}</td>
                    <td>
                      <span
                        className={`courier-status ${
                          record.status === "Delivered"
                            ? "courier-inside"
                            : "courier-exited"
                        }`}
                      >
                        {record.status}
                      </span>
                    </td>
                    <td>{record.remarks}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={16} className="courier-no-data">
                    No Records Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ICourierReport;
